import { FlexColumn, LabeledInput, MaterialIcon, StyledButton } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { REQUIRED_FIELD_TEXT } from '../../../constants/app';
import { useForms } from '../../../hooks/useForms';
import { usePasswordValidation } from '../../../hooks/usePasswordValidation';
import { useStyles } from '../../../hooks/useStyles';
import {
  editConfirmPasswordUserState,
  editUserState,
  editUserTouchedState,
  editUserValidState,
} from '../../../storage/userStates';
import { User, UserRequest, UserRequestTouched } from '../../../types/users';
import { CompanySelect } from '../../companies/CompanySelect';
import { TenantSelect } from '../../tenants/TenantSelect';
import { LabeledEmailInput } from '../../ui/LabeledEmailInput';
import { LabeledPhoneInput } from '../../ui/LabeledPhoneInput';

type Props = {
  user?: User;
};
export function UserFormFields({ user }: Readonly<Props>) {
  const editMode = user !== undefined;
  const { css, theme } = useStyles();
  const [form, setForm] = useRecoilState(editUserState);
  const [formTouched, setFormTouched] = useRecoilState(editUserTouchedState);
  const [confirmPassword, setConfirmPassword] = useRecoilState(editConfirmPasswordUserState);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const formValid = useRecoilValue(editUserValidState);
  const { passwordValidation } = usePasswordValidation(form.password, confirmPassword);
  const { onTouched } = useForms<UserRequest, UserRequestTouched>(setForm, setFormTouched);

  const onChangeInput = (key: keyof UserRequest, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <FlexColumn
      gap={theme.sizing.scale500}
      classNames={css({
        overflow: 'auto',
        height: '100%',
      })}
    >
      <LabeledInput
        label="Nombre(s)"
        placeholder="Ingresa nombre(s) del usuario"
        value={form.firstName}
        onChange={(value) => {
          onChangeInput('firstName', value);
        }}
        onBlur={() => onTouched('firstNameTouched')}
        error={formTouched.firstNameTouched && !formValid.firstName && REQUIRED_FIELD_TEXT}
        required
      />
      <LabeledInput
        label="Apellido(s)"
        placeholder="Ingresa apellido(s) del usuario "
        value={form.lastName}
        onChange={(value) => {
          onChangeInput('lastName', value);
        }}
        onBlur={() => onTouched('lastNameTouched')}
        error={formTouched.lastNameTouched && !formValid.lastName && REQUIRED_FIELD_TEXT}
        required
      />
      <TenantSelect
        selectedTenantId={form.tenant ? form.tenant : undefined}
        tenant={user?.tenant}
        onChangeTenantId={(value) => {
          onChangeInput('tenant', value);
        }}
        showEmptyOption
        isFilter={false}
        clearable
      />
      <CompanySelect
        selectedCompanyId={form.company ? form.company : undefined}
        company={user?.company}
        onChangeCompanyId={(value) => {
          onChangeInput('company', value);
        }}
        showEmptyOption
        isFilter={false}
        clearable
      />
      <LabeledEmailInput
        label="Correo electrónico"
        placeholder="Ingresa correo electrónico del usuario"
        onChange={(value) => {
          onChangeInput('email', value);
        }}
        value={form.email}
        required
        disabled={editMode}
      />
      <LabeledPhoneInput
        label="Telefono"
        placeholder="Ingresa el telefono del usuario"
        value={form.phone}
        onChange={(phone) => onChangeInput('phone', phone)}
        type="simple"
      />
      {editMode && (
        <Checkbox
          checked={form.active}
          onChange={(e) => onChangeInput('active', e.target.checked)}
        >
          Activo
        </Checkbox>
      )}
      {editMode && !changePassword && (
        <StyledButton
          kind="secondary"
          onClick={() => {
            setChangePassword(true);
          }}
        >
          Change password
        </StyledButton>
      )}
      {(!editMode || changePassword) && (
        <>
          <LabeledInput
            label="Contraseña *"
            placeholder="Ingresa la contraseña del usuario"
            value={form.password}
            onChange={(value) => {
              onChangeInput('password', value);
            }}
            onBlur={() => onTouched('passwordTouched')}
            error={formTouched.passwordTouched && !form.password ? REQUIRED_FIELD_TEXT : passwordValidation('errors')}
            startEnhancer={<MaterialIcon name="lock" />}
            type="password"
          />
          <LabeledInput
            label="Confirmar contraseña *"
            placeholder="Ingresa nuevamente la contraseña del usuario"
            value={confirmPassword}
            onChange={(value) => {
              setConfirmPassword(value);
            }}
            onBlur={() => setConfirmPasswordTouched(true)}
            error={
              confirmPasswordTouched && !confirmPassword
                ? REQUIRED_FIELD_TEXT
                : passwordValidation('repeatPasswordErrors')
            }
            startEnhancer={<MaterialIcon name="lock" />}
            type="password"
          />
        </>
      )}
    </FlexColumn>
  );
}
